import { useEffect } from 'react';

/**
 * Sayfa başlığı ve meta etiketlerini yöneten hafif bileşen.
 *
 * NOT (docs/02 §1 sapması): doküman `react-helmet-async` diyor. O paket 2.0.5'te
 * bakımsız kaldı ve bu kurulumda hiçbir etiket uygulamıyor (izole testte de doğrulandı);
 * bakımlı çatalı `@dr.pogodin/react-helmet` ise React 19 istiyor, biz React 18'deyiz.
 * İhtiyacımız başlık + meta + JSON-LD ile sınırlı olduğu için bağımlılık yerine bu
 * ~80 satırlık yönetici yazıldı. SSR'a geçilirse (docs/02 "ileride ayrı karar")
 * yeniden değerlendirilmeli.
 *
 * Yönettiği etiketler `data-seo` ile işaretlenir; sayfa değişince temizlenir.
 */

const MANAGED_ATTRIBUTE = 'data-seo';

export interface SeoProps {
  /** Tarayıcı sekmesi ve arama sonucu başlığı. */
  title: string;
  description?: string | undefined;
  /** Panel/auth gibi dizine girmemesi gereken sayfalar için. */
  noIndex?: boolean;
  /** Sosyal paylaşım etiketleri: `{ 'og:image': '...' }` */
  og?: Record<string, string | undefined>;
  /** schema.org yapılandırılmış verisi (JSON-LD). */
  jsonLd?: unknown;
  /** Kanonik adres; verilmezse mevcut yol kullanılır. */
  canonical?: string;
}

/** `/uploads/...` gibi göreli adresleri mutlak hâle getirir. */
function toAbsoluteUrl(value: string): string {
  return value.startsWith('http') ? value : new URL(value, window.location.origin).toString();
}

interface TagSpec {
  tag: 'meta' | 'link' | 'script';
  attrs: Record<string, string>;
  text?: string;
}

function buildTags(props: SeoProps): TagSpec[] {
  const tags: TagSpec[] = [];

  if (props.description) {
    tags.push({ tag: 'meta', attrs: { name: 'description', content: props.description } });
  }

  if (props.noIndex) {
    tags.push({ tag: 'meta', attrs: { name: 'robots', content: 'noindex' } });
  }

  // Dizine girmeyen sayfalara kanonik adres eklemiyoruz.
  if (!props.noIndex) {
    tags.push({
      tag: 'link',
      attrs: {
        rel: 'canonical',
        // Kanonik adres MUTLAK olmak zorunda; sayfalar "/ilanlar" gibi göreli değer veriyor.
        href: toAbsoluteUrl(props.canonical ?? window.location.pathname),
      },
    });
  }

  const og: Record<string, string | undefined> = {
    'og:title': props.title,
    'og:description': props.description,
    'og:locale': 'tr_TR',
    'og:url': window.location.href,
    ...props.og,
  };

  for (const [property, content] of Object.entries(og)) {
    if (!content) continue;
    // Sosyal ağlar göreli adresi çözemez; görsel ve adresler mutlak olmalı.
    const value =
      property === 'og:image' || property === 'og:url' ? toAbsoluteUrl(content) : content;
    tags.push({ tag: 'meta', attrs: { property, content: value } });
  }

  if (props.jsonLd) {
    tags.push({
      tag: 'script',
      attrs: { type: 'application/ld+json' },
      text: JSON.stringify(props.jsonLd),
    });
  }

  return tags;
}

export function Seo(props: SeoProps) {
  const { title, description, noIndex, canonical, jsonLd } = props;
  // Nesne referansı her render'da değişmesin diye bağımlılıkta serileştirilmiş hâli kullanılıyor.
  const ogKey = JSON.stringify(props.og ?? {});
  const jsonLdKey = JSON.stringify(jsonLd ?? null);

  useEffect(() => {
    document.title = title;

    // Önceki sayfanın etiketlerini kaldır.
    for (const element of document.head.querySelectorAll(`[${MANAGED_ATTRIBUTE}]`)) {
      element.remove();
    }

    const created: Element[] = [];
    for (const spec of buildTags({
      title,
      description,
      noIndex,
      canonical,
      jsonLd,
      og: JSON.parse(ogKey) as Record<string, string>,
    })) {
      const element = document.createElement(spec.tag);
      for (const [name, value] of Object.entries(spec.attrs)) {
        element.setAttribute(name, value);
      }
      if (spec.text) element.textContent = spec.text;
      element.setAttribute(MANAGED_ATTRIBUTE, '');
      document.head.appendChild(element);
      created.push(element);
    }

    return () => {
      for (const element of created) element.remove();
    };
  }, [title, description, noIndex, canonical, ogKey, jsonLdKey, jsonLd]);

  return null;
}
